import { useState } from "react";
import { useFormik } from "formik";
import _ from "lodash";

import theme from "theme";
import { colors } from "theme/palette";

import Button from "components/Button";
import Checkbox from "components/Checkbox";
import InputLabel from "components/InputLabel";
import Link from "components/Link";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";

import { ICompanyAutocompleteOption } from "entities/Company/sdk";

import { VALIDATION_SCHEMA } from "./constants";

export interface IFormValues {
  first_name: string;
  last_name: string;
  company_id: number | null;
  company_name: string;
  company_website: string | null;
  create_approved_email_domain: boolean;
}

export interface IOnboardingForm {
  emailDomain: string;
  selectedCompany: ICompanyAutocompleteOption | null;
  handleSubmit: ({ values }: { values: IFormValues }) => void;
  showAutomaticApprovedEmailDomainCreationCheckbox: boolean;
}

const OnboardingForm = ({
  emailDomain,
  handleSubmit,
  selectedCompany,
  showAutomaticApprovedEmailDomainCreationCheckbox,
}: IOnboardingForm) => {
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const formik = useFormik<IFormValues>({
    initialValues: {
      first_name: "",
      last_name: "",
      company_id: _.get(selectedCompany, "id", null),
      company_name: _.get(selectedCompany, "name", ""),
      company_website: _.get(selectedCompany, "website", ""),
      create_approved_email_domain: false,
    },
    onSubmit: (values) => handleSubmit({ values }),
    validationSchema: VALIDATION_SCHEMA,
  });

  const toggleTerms = () => {
    setHasAcceptedTerms((prevValue) => !prevValue);
  };

  return (
    <form onSubmit={formik.handleSubmit} data-testid="signup-form">
      <Stack direction="row" spacing={3} marginBottom={4}>
        <Stack width="100%">
          <InputLabel
            sx={{
              color: colors.black,
              fontWeight: 500,
              marginBottom: theme.spacing(0.5),
            }}
            data-testid="first-name-label"
          >
            First name
          </InputLabel>
          <TextField
            variant="outlined"
            placeholder="First name"
            {...formik.getFieldProps("first_name")}
            error={formik.touched.first_name && !!formik.errors.first_name}
            helperText={
              formik.touched.first_name && formik.errors.first_name
                ? formik.errors.first_name
                : ""
            }
            data-testid="first-name"
            inputProps={{ "data-testid": "first-name-input" }}
          />
        </Stack>
        <Stack width="100%">
          <InputLabel
            sx={{
              color: colors.black,
              fontWeight: 500,
              marginBottom: theme.spacing(0.5),
            }}
            data-testid="last-name-label"
          >
            Last name
          </InputLabel>
          <TextField
            variant="outlined"
            placeholder="Last name"
            {...formik.getFieldProps("last_name")}
            error={formik.touched.last_name && !!formik.errors.last_name}
            helperText={
              formik.touched.last_name && formik.errors.last_name
                ? formik.errors.last_name
                : ""
            }
            data-testid="last-name"
            inputProps={{ "data-testid": "last-name-input" }}
          />
        </Stack>
      </Stack>
      <Stack direction="row" spacing={3}>
        <Stack width="100%">
          <InputLabel
            sx={{
              color: colors.black,
              fontWeight: 500,
              marginBottom: theme.spacing(0.5),
            }}
            data-testid="company-name-label"
          >
            Company name
          </InputLabel>
          <TextField
            {...formik.getFieldProps("company_name")}
            placeholder="Company name"
            sx={{ width: "100%" }}
            error={formik.touched.company_name && !!formik.errors.company_name}
            helperText={
              formik.touched.company_name && formik.errors.company_name
                ? formik.errors.company_name
                : ""
            }
            data-testid="new-company-name"
            inputProps={{ "data-testid": "new-company-name-input" }}
            disabled={!_.isNil(selectedCompany)}
          />
        </Stack>
        <Stack width="100%">
          <InputLabel
            sx={{
              color: colors.black,
              fontWeight: 500,
              marginBottom: theme.spacing(0.5),
            }}
            data-testid="company-website-label"
          >
            Company website
          </InputLabel>

          <TextField
            {...formik.getFieldProps("company_website")}
            placeholder="Company website"
            data-testid="company-website"
            inputProps={{ "data-testid": "company-website-input" }}
            disabled={!_.isNil(selectedCompany)}
          />
        </Stack>
      </Stack>

      <Stack marginTop={6} marginBottom={6}>
        {showAutomaticApprovedEmailDomainCreationCheckbox && (
          <Stack direction="row" alignItems="center">
            <Checkbox
              checked={formik.values.create_approved_email_domain}
              sx={{ backgrouncColor: colors.white }}
              onClick={() =>
                formik.setFieldValue(
                  "create_approved_email_domain",
                  !formik.values.create_approved_email_domain
                )
              }
            />
            <Text>
              I would like to claim this email domain(<b>@{emailDomain}</b>) as
              my company's own. I understand that any new user signing up with
              an email from that domain will automatically be included as part
              of my company on Builders Patch. (Optional)
            </Text>
          </Stack>
        )}

        <Stack direction="row" alignItems="center">
          <Checkbox
            checked={hasAcceptedTerms}
            sx={{ backgrouncColor: colors.white }}
            onClick={toggleTerms}
            data-testid="accept-terms-checkbox"
          />
          <Text data-testid="accept-terms-text">
            By signing up, I accept the Builders Patch{" "}
            <Link
              external
              target="_blank"
              to="https://builderspatch.com/terms"
              style={{ color: colors.blue100 }}
              data-testid="terms-link"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              external
              target="_blank"
              to="https://builderspatch.com/privacy"
              style={{ color: colors.blue100 }}
              data-testid="privacy-link"
            >
              Privacy Policy
            </Link>
            .
          </Text>
        </Stack>

        <Button
          type="submit"
          disabled={!_.isEmpty(formik.errors) || !hasAcceptedTerms}
          data-testid="submit-button"
          sx={{ alignSelf: "flex-end" }}
        >
          Create profile
        </Button>
      </Stack>
    </form>
  );
};

export default OnboardingForm;
